import { useState, useRef, useEffect } from "react"

import { getImportProgress, ImportProgressResponseData } from "services/import"

interface UseImportPollingProps {
  pmcId: string
  pmsType: string
  intervalMs: number
}

const MAX_FAILURES = 3

export const useImportPolling = ({
  pmcId,
  pmsType,
  intervalMs,
}: UseImportPollingProps) => {
  const [data, setData] = useState<ImportProgressResponseData["data"] | null>(
    null,
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const failureCountRef = useRef(0)

  const isCompleted = data?.overallStatus === "COMPLETED"
  const isFailed = data?.overallStatus === "FAILED"

  useEffect(() => {
    let intervalId: NodeJS.Timeout
    let isMounted = true

    const fetchProgress = async () => {
      if (!pmcId || !pmsType) return

      try {
        const res = await getImportProgress({ params: { pmcId, pmsType } })
        if (isMounted) {
          const apiResponse = res.data

          if (
            apiResponse.apiStatus === "FAILED" ||
            apiResponse.apiStatus === "ERROR"
          ) {
            setError(
              apiResponse.message ||
                apiResponse.errorMessage ||
                "Import process failed on the server.",
            )
            setLoading(false)
            return
          }

          setData(apiResponse.data)
          failureCountRef.current = 0 // Reset failures on success
          setLoading(false)
        }
      } catch (err: unknown) {
        if (isMounted) {
          failureCountRef.current += 1
          if (failureCountRef.current >= MAX_FAILURES) {
            const errObj = err as { message?: string; errorMessage?: string }
            const apiErrorMsg =
              errObj?.message ||
              errObj?.errorMessage ||
              "Failed to fetch import progress continually."
            setError(apiErrorMsg)
            setLoading(false)
          }
        }
      }
    }

    if (!isCompleted && !isFailed && error === null) {
      if (!data) setLoading(true)

      // Initial fetch
      fetchProgress().catch(() => undefined)

      // Polling setup
      intervalId = setInterval(() => {
        fetchProgress().catch(() => undefined)
      }, intervalMs)
    }

    return () => {
      isMounted = false
      if (intervalId) clearInterval(intervalId)
    }
  }, [isCompleted, isFailed, error, pmcId, pmsType, intervalMs])

  const handleRetry = () => {
    setLoading(true)
    setError(null)
    failureCountRef.current = 0
  }

  return {
    data,
    loading,
    error,
    isCompleted,
    isFailed,
    handleRetry,
    steps: data?.steps || [],
  }
}
